import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import api from "@/lib/api";

/**
 * Phase H.5 — shared "Log in as" trigger.
 * - Only rendered when the caller is super_admin.
 * - Never renders when already impersonating (UI-level chain block; backend
 *   also enforces).
 * - Never renders when the target is another super_admin or the acting user
 *   themselves — parent must pass a valid target.
 *
 * Two modes:
 *   - `targetUserId` (admin rows): calls POST /api/admin/impersonate/{id} directly.
 *   - `customerId` (customer rows / detail page): first resolves the customer's
 *     login user via /admin/customers/{id}/login-user, then impersonates. If
 *     no login user exists yet (customer never signed up), the button stays
 *     disabled with a tooltip explaining why.
 */
export function LogInAsButton({
    targetUserId, customerId,
    targetEmail, targetRole = "user",
    variant = "outline", size = "sm",
    className = "", labelOverride,
    dataTestid,
}) {
    const { user, startImpersonation } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [busy, setBusy] = useState(false);

    if (!user || user.role !== "super_admin") return null;
    if (user.impersonating) return null;
    if (!targetUserId && !customerId) return null;
    if (targetUserId && targetUserId === user.id) return null;

    const onClick = async () => {
        if (busy) return;
        setBusy(true);
        try {
            let resolvedId = targetUserId;
            if (!resolvedId && customerId) {
                try {
                    const { data } = await api.get(`/admin/customers/${customerId}/login-user`);
                    if (data?.deactivated) {
                        toast.error("Cannot impersonate — customer's login is deactivated.");
                        return;
                    }
                    resolvedId = data.user_id;
                } catch (err) {
                    const detail = err?.response?.data?.detail || "Customer has no portal login yet.";
                    toast.error(typeof detail === "string" ? detail : "Customer has no portal login yet.");
                    return;
                }
            }
            const imp = await startImpersonation(resolvedId, location.pathname);
            toast.success(`Viewing as ${imp.target_email} (${imp.target_role}).`);
            navigate(imp.target_role === "customer" ? "/customer" : "/admin");
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Failed to impersonate";
            toast.error(typeof detail === "string" ? detail : "Failed to impersonate");
        } finally {
            setBusy(false);
        }
    };

    const label = labelOverride || (targetRole === "customer" ? "Log in as customer" : "Log in as");
    const testid = dataTestid || `login-as-${targetUserId || customerId}`;
    return (
        <Button
            type="button"
            variant={variant}
            size={size}
            onClick={onClick}
            disabled={busy}
            data-testid={testid}
            className={`gap-1.5 ${className}`}
            title={targetEmail ? `Log in as ${targetEmail}` : "Log in as this user"}
        >
            <LogIn size={13} />
            {busy ? "Opening…" : label}
        </Button>
    );
}
